import dayjs from 'dayjs'
import type { FormInstance as Form } from 'antd'
import type { InvoiceFormValues } from '@/api/useSupplierReconciliation/types/forms'

export function useInvoiceValidation() {
  const getInvoiceNumberRules = () => [
    {
      required: true,
      message: 'Please enter invoice number',
    },
  ]

  const getSupplierRules = () => [{ required: true, message: 'Please select supplier' }]

  const getInvoiceDateRules = (form: Form<InvoiceFormValues>) => [
    {
      required: true,
      message: 'Please select invoice date',
    },
    {
      validator: (_: unknown, value: dayjs.Dayjs) => {
        if (value && dayjs(value).isAfter(dayjs(), 'day')) {
          return Promise.reject(new Error('Invoice date cannot be in the future'))
        }

        const invoiceDue = form.getFieldValue('invoiceDue')

        if (invoiceDue) {
          setTimeout(() => {
            form.validateFields(['invoiceDue'])
          }, 0)
        }

        return Promise.resolve()
      },
    },
  ]

  const getInvoiceDueRules = (form: Form<InvoiceFormValues>) => [
    { required: true, message: 'Please select due date' },
    {
      validator: (_: unknown, value: dayjs.Dayjs) => {
        const invoiceDate = form.getFieldValue('invoiceDate')
        if (value && invoiceDate && dayjs(value).isBefore(dayjs(invoiceDate))) {
          return Promise.reject(new Error('Due date cannot be earlier than invoice date'))
        }
        return Promise.resolve()
      },
    },
  ]

  const getTerminalRules = () => [
    {
      required: true,
      message: 'Please select terminal',
    },
  ]

  const getBillingMethodRules = () => [
    {
      required: true,
      message: 'Please select billing method',
    },
  ]

  const getProductRules = () => [
    {
      required: true,
      message: 'Select product',
    },
  ]

  const getBOLNumberRules = () => [
    {
      required: true,
      message: 'Enter BOL number',
    },
  ]

  const getBOLDateTimeRules = () => [
    {
      required: true,
      message: 'Select BOL date and time',
    },
  ]

  const getGrossVolumeRules = (form: Form<InvoiceFormValues>, index: number) => [
    {
      required: true,
      message: 'Enter gross volume',
    },
    {
      validator: (_: unknown, value: number) => {
        const products = form.getFieldValue('products') || []
        const currentProduct = products[index]
        const netVol = currentProduct?.netVol

        if (netVol) {
          setTimeout(() => {
            form.validateFields([['products', index, 'netVol']])
          }, 0)
        }

        return Promise.resolve()
      },
    },
  ]

  const getNetVolumeRules = (form: Form<InvoiceFormValues>, index: number) => [
    {
      required: true,
      message: 'Enter net volume',
    },
  ]

  const getUnitPriceRules = () => [
    {
      required: true,
      message: 'Enter unit price',
    },
  ]

  const getTaxDescriptionRules = () => [{ required: true, message: 'Enter tax description' }]

  const getTaxTypeRules = () => [{ required: true, message: 'Select tax type' }]

  const getTaxAuthorityRules = () => [
    // Tax Authority is now optional per KB-33739
  ]

  const validateFormSubmission = (form: Form<InvoiceFormValues>) => {
    const values = form.getFieldsValue()

    const hasProducts = values.products?.length > 0
    const hasTaxes = values.taxes?.length > 0

    if (!hasProducts && !hasTaxes) {
      form.setFields([
        {
          name: 'products',
          errors: ['At least one product or tax line is required'],
        },
      ])
      return false
    }

    return true
  }

  const validateNetVolumeAgainstGross = (
    form: Form<InvoiceFormValues>,
    index: number,
    field: 'netVol' | 'grossVol',
    value: number | string
  ) => {
    return true
  }

  return {
    getInvoiceNumberRules,
    getSupplierRules,
    getInvoiceDateRules,
    getInvoiceDueRules,
    getTerminalRules,
    getBillingMethodRules,
    getProductRules,
    getGrossVolumeRules,
    getNetVolumeRules,
    getUnitPriceRules,
    getTaxDescriptionRules,
    getTaxTypeRules,
    getTaxAuthorityRules,
    validateFormSubmission,
    validateNetVolumeAgainstGross,
    getBOLDateTimeRules,
    getBOLNumberRules,
  }
}
